import { Link } from "react-router-dom";
import {
  MessageCircle,
  Leaf,
  Home as HomeIcon,
  Truck,
  XCircle,
  ChefHat,
  CheckCircle2,
  Sparkles,
  Star,
  Users,
  Calendar,
  Flame,
  Droplet,
  Wheat,
  Salad,
  Cookie,
  ArrowRight,
  Quote,
} from "lucide-react";
import Reveal from "../components/Reveal";
import LocationSection from "../components/LocationSection";
import { BRAND, waOrder } from "../lib/brand";

const HERO_IMG = "https://images.pexels.com/photos/29148133/pexels-photo-29148133.jpeg";
const THALI_IMG = "https://images.pexels.com/photos/9738980/pexels-photo-9738980.jpeg";
const VEG_IMG = "https://images.pexels.com/photos/5425892/pexels-photo-5425892.jpeg";
const SPICES_IMG = "https://images.pexels.com/photos/2632292/pexels-photo-2632292.jpeg";
const EVENT_IMG = "https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg";

const WHY_ITEMS = [
  {
    icon: Leaf,
    title: "100% Pure & Organic",
    desc: "Farm-fresh, unadulterated ingredients. Nothing you wouldn't feed your own family.",
  },
  {
    icon: HomeIcon,
    title: "Tastes Just Like Home",
    desc: "The comforting flavours of a mother's kitchen — slow-cooked and heartfelt.",
  },
  {
    icon: Truck,
    title: "Free Doorstep Delivery",
    desc: "Piping hot meals delivered on time, every single day, at no extra charge.",
  },
  {
    icon: XCircle,
    title: "No Junk, No Adulteration",
    desc: "Zero preservatives, no cheap oils, no shortcuts. Only what your body deserves.",
  },
  {
    icon: ChefHat,
    title: "Freshly Made Daily",
    desc: "Cooked each morning in small batches. Never frozen, never reheated from yesterday.",
  },
  {
    icon: CheckCircle2,
    title: "Customisable Meals",
    desc: "Low-oil, jain, less spicy, extra rotis — tell us, we make it your way.",
  },
];

const THALI_ITEMS = [
  { icon: Wheat, label: "4 Soft Rotis" },
  { icon: Flame, label: "Seasonal Veg Sabzi" },
  { icon: Droplet, label: "Dal or Curry" },
  { icon: Salad, label: "Steamed Rice" },
  { icon: Leaf, label: "Fresh Salad" },
  { icon: Cookie, label: "Fresh Dessert" },
];

const JUNK_SIDE = [
  "Deep-fried in reused oil",
  "Loaded with preservatives",
  "Artificial colours & MSG",
  "Made in dirty, anonymous kitchens",
  "Triggers acidity, bloating, weight gain",
  "Tastes good. Feels bad.",
];
const PURE_SIDE = [
  "Cooked in cold-pressed mustard & ghee",
  "Zero preservatives, ever",
  "Whole spices ground fresh each week",
  "Made in Archna's own home kitchen",
  "Light on the stomach, heavy on flavour",
  "Tastes like love. Feels like home.",
];

const TESTIMONIALS = [
  {
    name: "Ritu Malhotra",
    role: "Working Professional, Saket",
    quote:
      "I've been eating Archna's tiffin for four months. It's the first time in years I finish my lunch and don't feel heavy. Exactly like my mom's khaana.",
    rating: 5,
  },
  {
    name: "Ankit Verma",
    role: "Software Engineer, Gurgaon",
    quote:
      "Stopped ordering from food apps completely. Fresh rotis, proper sabzi, real dal — all for ₹225. The salad is so fresh, I actually look forward to it.",
    rating: 5,
  },
  {
    name: "Sunita Sharma",
    role: "Homemaker, Delhi",
    quote:
      "Ordered for my daughter's small birthday get-together. Guests kept asking who made it. The chole bhature tasted better than any caterer I've tried.",
    rating: 5,
  },
];

export default function Home() {
  return (
    <div className="bg-[#faf8f5]">
      {/* ============ HERO ============ */}
      <section
        className="relative min-h-[100svh] pt-[72px] overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="An abundant home-cooked Indian thali on a wooden table"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1530]/95 via-[#1a2e5a]/85 to-[#1a2e5a]/40 md:to-transparent" />
          <div className="absolute inset-0 mandala-bg opacity-[0.07] mix-blend-luminosity" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-28 pb-20 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8 lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 bg-[#c9a84c]/15 border border-[#c9a84c]/40 text-[#c9a84c] px-4 py-2 rounded-full text-[11px] tracking-[0.28em] uppercase font-bold mb-6">
                <Sparkles size={14} /> Delhi's Home-Cooked Tiffin
              </div>
            </Reveal>

            <Reveal delay={1}>
              <h1 className="hero-title text-white text-[2.6rem] sm:text-5xl md:text-6xl lg:text-7xl mb-6">
                Real Food.
                <br />
                Pure Ingredients.
                <br />
                <span className="text-[#c9a84c] italic">Delivered</span> to your door.
              </h1>
            </Reveal>

            <Reveal delay={2}>
              <p className="font-body text-white/80 text-base md:text-lg max-w-xl leading-relaxed mb-8">
                While the world orders oily junk that harms the body, we
                quietly cook the way your mother would —{" "}
                <span className="text-[#c9a84c] font-semibold">
                  organic, unadulterated, fresh
                </span>{" "}
                — and send it straight to your dining table.
              </p>
            </Reveal>

            <Reveal delay={3}>
              <div className="flex flex-wrap gap-4">
                <a
                  href={waOrder("Hi! I'd like to order today's thali from Archna's Kitchen.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold"
                  data-testid="hero-whatsapp-cta"
                >
                  <MessageCircle size={18} strokeWidth={2.5} />
                  Order Now on WhatsApp
                </a>
                <Link to="/menu" className="btn-ghost-gold" data-testid="hero-view-menu">
                  View Menu
                  <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={4}>
              <div className="flex flex-wrap gap-6 md:gap-10 mt-12 text-white/70 text-xs tracking-[0.15em] uppercase">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#c9a84c]" />
                  Free Delivery
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#c9a84c]" />
                  Freshly Cooked
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#c9a84c]" />
                  Organic Only
                </div>
              </div>
            </Reveal>
          </div>

          {/* Price badge */}
          <div className="md:col-span-4 lg:col-span-5 flex md:justify-end">
            <Reveal delay={3}>
              <div className="float-slow relative bg-[#faf8f5] rounded-3xl shadow-2xl border border-[#c9a84c]/40 p-7 md:p-8 max-w-xs" data-testid="hero-price-badge">
                <div className="absolute -top-3 left-6 bg-[#c9a84c] text-[#1a2e5a] text-[10px] tracking-[0.25em] uppercase font-black px-3 py-1 rounded-full">
                  Today's Thali
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#1a2e5a]/60 mb-2">
                  All-inclusive
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-6xl font-bold text-[#1a2e5a]">
                    ₹225
                  </span>
                  <span className="text-xs text-[#1a2e5a]/60">/ thali</span>
                </div>
                <div className="h-px bg-[#c9a84c]/30 my-4" />
                <div className="text-sm text-[#1a2e5a]/70 font-body leading-relaxed">
                  4 Rotis · Sabzi · Dal · Rice · Salad · Dessert — all fresh,
                  all home-made.
                </div>
                <div className="mt-4 text-[11px] text-[#c9a84c] font-bold tracking-[0.2em] uppercase">
                  + Free Doorstep Delivery
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#faf8f5] pointer-events-none" />
      </section>

      {/* ============ WHY ARCHNA'S ============ */}
      <section className="relative py-20 md:py-32 bg-[#faf8f5]" data-testid="why-section">
        <div className="absolute inset-0 spice-dots opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Reveal>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
                Why Archna's Kitchen
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#1a2e5a] leading-tight mb-5">
                Six reasons your body will{" "}
                <span className="italic text-[#c9a84c]">thank you</span>.
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="font-body text-slate-600 text-lg">
                Not every tiffin service is the same. We built ours around one
                simple belief — that real food, made slowly and honestly,
                changes the way you feel every single day.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {WHY_ITEMS.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={((i % 3) + 1)}>
                  <div
                    className="card-lift bg-white border border-[#c9a84c]/25 rounded-2xl p-7 md:p-8 h-full"
                    data-testid={`why-card-${i}`}
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[#1a2e5a] flex items-center justify-center mb-5">
                      <Icon size={24} className="text-[#c9a84c]" strokeWidth={1.8} />
                    </div>
                    <h3 className="font-display text-xl font-semibold text-[#1a2e5a] mb-2">
                      {item.title}
                    </h3>
                    <p className="font-body text-slate-600 text-[15px] leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ DAILY THALI ============ */}
      <section className="relative py-20 md:py-32 bg-[#1a2e5a] text-white overflow-hidden" data-testid="daily-thali-section">
        <div className="absolute inset-0 mandala-bg opacity-[0.06] pointer-events-none" />
        <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-[#0a1530] to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
                Daily Thali — Lunch & Dinner
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
                Everything your body wants.{" "}
                <span className="italic text-[#c9a84c]">₹225 only.</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="font-body text-white/75 text-lg leading-relaxed mb-8">
                A full, satisfying meal — exactly what a home kitchen would
                serve. No skimping, no frozen fillers. Just rotis straight off
                the tawa, sabzi simmered with whole spices, and a sweet little
                ending.
              </p>
            </Reveal>

            <Reveal delay={3}>
              <ul className="grid grid-cols-2 gap-3 mb-8">
                {THALI_ITEMS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <li
                      key={t.label}
                      className="flex items-center gap-3 bg-white/5 border border-[#c9a84c]/20 rounded-xl px-4 py-3"
                    >
                      <Icon size={18} className="text-[#c9a84c]" />
                      <span className="font-body text-sm text-white/90">
                        {t.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            <Reveal delay={4}>
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <span className="inline-flex items-center gap-2 bg-[#c9a84c] text-[#1a2e5a] text-[11px] font-black tracking-[0.2em] uppercase px-4 py-2 rounded-full">
                  <Sparkles size={14} strokeWidth={2.5} />
                  Customise at Nominal Extra Cost
                </span>
                <span className="inline-flex items-center gap-2 bg-white/10 border border-[#c9a84c]/30 text-white/80 text-[11px] tracking-[0.2em] uppercase px-4 py-2 rounded-full">
                  Breakfast on Special Order
                </span>
              </div>
            </Reveal>

            <Reveal delay={5}>
              <div className="flex flex-wrap gap-4">
                <a
                  href={waOrder("Hi! I'd like to order the daily thali (₹225). Please share today's menu.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold"
                  data-testid="thali-whatsapp-cta"
                >
                  <MessageCircle size={18} strokeWidth={2.5} />
                  Order Thali Now
                </a>
                <Link to="/menu" className="btn-ghost-gold" data-testid="thali-view-menu">
                  Full Menu
                  <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6">
            <Reveal delay={2}>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#c9a84c]/20 rounded-[2rem] blur-2xl" />
                <img
                  src={THALI_IMG}
                  alt="Fresh home-cooked Indian thali with rotis, dal, sabzi and dessert"
                  className="relative rounded-[2rem] shadow-2xl w-full h-[380px] md:h-[520px] object-cover border-2 border-[#c9a84c]/30"
                />
                <div className="absolute -bottom-6 -left-6 bg-[#faf8f5] rounded-2xl p-5 shadow-xl border border-[#c9a84c]/40 max-w-[200px]">
                  <div className="flex items-center gap-1 text-[#c9a84c] mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" />
                    ))}
                  </div>
                  <div className="text-[#1a2e5a] font-display italic text-sm leading-snug">
                    "Dal tastes exactly like my mother's. I'm hooked."
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ MONTHLY PACKAGE & BULK ============ */}
      <section className="relative py-20 md:py-32 bg-[#faf8f5]" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Reveal>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
                Packages & Bulk Orders
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#1a2e5a] leading-tight">
                Eat well <span className="italic text-[#c9a84c]">every day.</span>{" "}
                Celebrate well <span className="italic text-[#c9a84c]">every occasion.</span>
              </h2>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-12 gap-6 md:gap-8">
            {/* Monthly subscription */}
            <Reveal delay={1} className="md:col-span-7">
              <div
                className="card-lift relative h-full bg-[#1a2e5a] text-white rounded-3xl p-8 md:p-12 overflow-hidden"
                data-testid="monthly-package-card"
              >
                <div className="absolute inset-0 mandala-bg opacity-[0.05] pointer-events-none" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-[#c9a84c]/15 border border-[#c9a84c]/40 text-[#c9a84c] px-3 py-1.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-bold mb-6">
                    <Calendar size={12} /> Most Popular
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold mb-4">
                    Monthly Tiffin Package
                  </h3>
                  <p className="font-body text-white/75 text-base md:text-lg leading-relaxed mb-6">
                    Built for working professionals and busy families. Lock in
                    a lower daily rate, skip the "what's for dinner?" debate,
                    and let us feed you fresh home-style meals every single
                    day.
                  </p>
                  <ul className="space-y-2 mb-8">
                    {[
                      "Discounted pricing vs daily order",
                      "Lunch and/or dinner, you choose",
                      "Flexible pause & resume",
                      "Priority delivery windows",
                    ].map((b) => (
                      <li key={b} className="flex items-start gap-3 text-white/85 text-sm">
                        <CheckCircle2 size={16} className="text-[#c9a84c] mt-0.5" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={waOrder("Hi! I'd like to know about the monthly tiffin subscription pricing.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold"
                    data-testid="monthly-whatsapp-cta"
                  >
                    <MessageCircle size={16} strokeWidth={2.5} />
                    Get Monthly Pricing
                  </a>
                </div>
                <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-[#c9a84c]/10 blur-3xl pointer-events-none" />
              </div>
            </Reveal>

            {/* Bulk / Events */}
            <Reveal delay={2} className="md:col-span-5">
              <div
                className="card-lift relative h-full rounded-3xl overflow-hidden border border-[#c9a84c]/30"
                data-testid="bulk-events-card"
              >
                <img
                  src={EVENT_IMG}
                  alt="Home-style Indian food spread for a gathering"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a1530]/60 via-[#0a1530]/75 to-[#0a1530]/95" />
                <div className="relative p-8 md:p-10 h-full flex flex-col justify-end text-white min-h-[520px]">
                  <div className="inline-flex items-center gap-2 bg-[#c9a84c]/20 border border-[#c9a84c]/50 text-[#c9a84c] px-3 py-1.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-bold w-fit mb-5">
                    <Users size={12} /> Bulk Orders & Events
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4">
                    Feeding a crowd? <span className="italic text-[#c9a84c]">We've got you.</span>
                  </h3>
                  <p className="font-body text-white/80 text-sm md:text-base leading-relaxed mb-5">
                    Kitty parties, birthdays, anniversaries, pujas, small
                    gatherings — fresh, custom menus cooked in-house for every
                    occasion. No buffet-grade shortcuts, just real food in
                    generous portions.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {["Kitty Parties", "Birthdays", "Anniversaries", "Pujas", "Gatherings"].map(
                      (tag) => (
                        <span
                          key={tag}
                          className="text-[10px] tracking-[0.18em] uppercase bg-white/10 border border-[#c9a84c]/30 text-white/85 px-3 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      )
                    )}
                  </div>
                  <a
                    href={waOrder("Hi! I'd like to enquire about bulk/event catering from Archna's Kitchen.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold w-fit"
                    data-testid="bulk-whatsapp-cta"
                  >
                    <MessageCircle size={16} strokeWidth={2.5} />
                    Enquire on WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ JUNK VS PURE ============ */}
      <section className="relative py-20 md:py-32 bg-[#0a1530] text-white overflow-hidden" data-testid="comparison-section">
        <div className="absolute inset-0 mandala-bg opacity-[0.04] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <Reveal>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
                The Honest Comparison
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                Junk food vs{" "}
                <span className="italic text-[#c9a84c]">Archna's Kitchen.</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="font-body text-white/70 text-lg mt-5">
                No drama, no shaming. Just a side-by-side look at what you're
                actually eating.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Junk side */}
            <Reveal delay={1}>
              <div
                className="rounded-3xl p-8 md:p-10 bg-[#1a2340] border border-red-500/20 relative overflow-hidden h-full"
                data-testid="junk-card"
              >
                <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-red-500/10 blur-3xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-1.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-bold mb-6">
                    <XCircle size={12} /> Typical Food Delivery
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold mb-6">
                    Fast food, faster regret.
                  </h3>
                  <ul className="space-y-3">
                    {JUNK_SIDE.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-white/75">
                        <XCircle size={18} className="text-red-400 mt-0.5 shrink-0" />
                        <span className="font-body text-[15px]">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            {/* Pure side */}
            <Reveal delay={2}>
              <div
                className="rounded-3xl p-8 md:p-10 bg-[#132345] border border-[#c9a84c]/40 relative overflow-hidden h-full"
                data-testid="pure-card"
              >
                <img
                  src={VEG_IMG}
                  alt="Fresh organic vegetables"
                  className="absolute right-0 top-0 w-40 h-40 md:w-56 md:h-56 object-cover rounded-bl-[3rem] opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#132345] via-[#132345]/90 to-transparent" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-[#c9a84c]/20 border border-[#c9a84c]/50 text-[#c9a84c] px-3 py-1.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-bold mb-6">
                    <Leaf size={12} /> Archna's Kitchen
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold mb-6">
                    Slow cooking, quiet healing.
                  </h3>
                  <ul className="space-y-3">
                    {PURE_SIDE.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-white/90">
                        <CheckCircle2 size={18} className="text-[#c9a84c] mt-0.5 shrink-0" />
                        <span className="font-body text-[15px]">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={3}>
            <div className="text-center mt-14">
              <a
                href={waOrder("Hi! I'd like to switch from food apps to Archna's Kitchen. Please share details.")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid="comparison-whatsapp-cta"
              >
                <MessageCircle size={18} strokeWidth={2.5} />
                Switch to Real Food
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="relative py-20 md:py-32 bg-[#faf8f5]" data-testid="testimonials-section">
        <div className="absolute inset-0 spice-dots opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Reveal>
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
                Word of Mouth
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#1a2e5a] leading-tight">
                People who eat with us,{" "}
                <span className="italic text-[#c9a84c]">stay with us.</span>
              </h2>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-7">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i + 1}>
                <div
                  className="card-lift relative h-full bg-white border border-[#c9a84c]/25 rounded-2xl p-8"
                  data-testid={`testimonial-${i}`}
                >
                  <Quote size={28} className="text-[#c9a84c]/40 mb-4" />
                  <div className="flex items-center gap-1 text-[#c9a84c] mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} size={14} fill="currentColor" />
                    ))}
                  </div>
                  <p className="font-display italic text-[#1a2e5a] text-lg leading-relaxed mb-6">
                    "{t.quote}"
                  </p>
                  <div className="pt-4 border-t border-[#c9a84c]/20">
                    <div className="font-body font-bold text-[#1a2e5a] text-sm">
                      {t.name}
                    </div>
                    <div className="text-xs text-slate-500 tracking-wide">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ LOCATION ============ */}
      <LocationSection variant="light" />

      {/* ============ CONTACT / ORDER STRIP ============ */}
      <section
        className="relative py-20 md:py-24 bg-[#1a2e5a] text-white overflow-hidden"
        data-testid="order-contact-section"
      >
        <img
          src={SPICES_IMG}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1530] via-[#1a2e5a]/90 to-[#132345]" />
        <div className="absolute inset-0 mandala-bg opacity-[0.05] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
              Order & Contact
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              We take orders on{" "}
              <span className="italic text-[#c9a84c]">WhatsApp.</span>{" "}
              Just message us.
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="font-body text-white/75 text-lg max-w-2xl mx-auto mb-8">
              No apps, no forms, no waiting. Send us a WhatsApp message or give
              us a call — we'll confirm your order and deliver fresh, free of
              charge.
            </p>
          </Reveal>

          <Reveal delay={3}>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
              <a
                href={BRAND.whatsappChat}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid="contact-whatsapp-cta"
              >
                <MessageCircle size={18} strokeWidth={2.5} />
                Message on WhatsApp
              </a>
              <a
                href={BRAND.whatsappGroup}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost-gold"
                data-testid="contact-whatsapp-group"
              >
                <Users size={18} strokeWidth={2.5} />
                Join WhatsApp Group
              </a>
            </div>
          </Reveal>

          <Reveal delay={4}>
            <div className="inline-flex items-center gap-3 bg-white/5 border border-[#c9a84c]/30 rounded-full px-6 py-3">
              <div className="w-9 h-9 rounded-full bg-[#c9a84c] flex items-center justify-center">
                <MessageCircle size={16} className="text-[#1a2e5a]" strokeWidth={2.5} />
              </div>
              <a
                href={`tel:+${BRAND.phoneRaw}`}
                className="font-display text-2xl md:text-3xl text-[#c9a84c] font-semibold tracking-wide"
                data-testid="contact-phone-display"
              >
                {BRAND.phoneDisplay}
              </a>
            </div>
          </Reveal>

          <Reveal delay={5}>
            <div className="mt-6 text-xs tracking-[0.25em] uppercase text-white/60">
              Free Home Delivery · Delhi-wide
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
